import jwt_decode from 'jwt-decode';
import type { ExternalUserType } from '../utilities/userRoles';

const AUTH_STORAGE_UPDATED_EVENT = 'auth:storage-updated';

const emitAuthStorageUpdated = () => {
  window.dispatchEvent(new Event(AUTH_STORAGE_UPDATED_EVENT));
};

const isValidRefreshToken = (token: unknown): token is string => {
  if (token == null || typeof token !== 'string') return false;

  const trimmed = token.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return false;

  try {
    jwt_decode(trimmed);
    return true;
  } catch {
    return false;
  }
};

const getLocalRefreshToken = (): string | null => {
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const token = user?.refresh_token;
  return isValidRefreshToken(token) ? token : null;
};

const getLocalAccessToken = () => {
  const access_token = localStorage.getItem('access_token');
  return access_token;
};

const setAccessToken = (new_token: string) => {
  localStorage.setItem('access_token', new_token);
  emitAuthStorageUpdated();
};

const updateNewAccessToken = (token: string) => {
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  if (user) {
    user.access_token = token;
    localStorage.setItem('user', JSON.stringify(user));
  }
  localStorage.setItem('access_token', token);
  emitAuthStorageUpdated();
};

const getUser = (): FastApiUser | null => {
  const userString = localStorage.getItem('user');
  return userString ? JSON.parse(userString) : null;
};

const setUser = (user: any) => {
  localStorage.setItem('user', JSON.stringify(user));

  if (isValidRefreshToken(user?.refresh_token)) {
    localStorage.setItem('authenticated', 'true');
  } else {
    localStorage.removeItem('authenticated');
  }

  emitAuthStorageUpdated();
};

const updateUserInfo = (userInfo: any) => {
  const userString = localStorage.getItem('user');
  let user = userString ? JSON.parse(userString) : null;
  if (user) {
    user = { ...user, ...userInfo };
    localStorage.setItem('user', JSON.stringify(user));
    emitAuthStorageUpdated();
  }
};

const TokenService = {
  getLocalRefreshToken,
  isValidRefreshToken,
  getLocalAccessToken,
  setAccessToken,
  updateNewAccessToken,
  getUser,
  setUser,
  updateUserInfo,
};

export default TokenService;

export interface TwoFactorAuth {
  auth_key: string;
  user_email: boolean;
  qr_code: boolean;
}

export interface UserUpdatedBy {
  ip_address: string;
  user_id: string;
}

export interface FastApiUser {
  access_token: string;
  refresh_token: string;
  auth_key: string;
  token_type: string;
  expires: number;

  user_id: string;
  user_name: string;
  user_type: ExternalUserType;
  user_client_id: string;
  user_client_ids: string[];
  user_parent_id: string;
  user_password: string;
  user_password_last_update: string;
  user_expiry_date: string | null;

  user_first_name: string | null;
  user_last_name: string | null;
  user_email: string | null;
  user_fund_ids: string[] | null;
  user_resource_ids: string[] | null;
  config_name: string | null;

  has_specific_funds?: boolean;
  has_specific_resources?: boolean;
  has_multi_clients?: boolean;

  ip_address?: string;

  user_two_factor_authentication?: TwoFactorAuth;
  user_updated_by?: UserUpdatedBy;
  user_updating_datetime?: string; // ISO datetime string
}
